import { useAppDispatch, useAppSelector } from 'app/store';
import { LoadAudioAction } from 'app/store/AudioPlayer/slice.types';
import { getAudioPlayer } from 'app/store/AudioPlayer/selectors';
import {
  closePlayerAction,
  loadAudioAction,
  setIsPlayingAction,
  setIsWaveformOpen,
  setRepeatAction,
  skipBackToPreviousAudioAction,
  skipToNextAudioAction,
} from 'app/store/AudioPlayer/slice';
import { useCallback } from 'react';
import * as Tone from 'tone';
import { loadWaveformData } from '../lib';

export const useAudioPlayer = () => {
  const {
    isPlaying: audioPlayerPlaying,
    queueIndex = 0,
    queue = [],
  } = useAppSelector(getAudioPlayer);
  const dispatch = useAppDispatch();
  const { player } = window;

  const stopAllPlayback = useCallback(() => {
    player?.stop();
    Tone.Transport.stop();
    Tone.Transport.position = 0;
  }, [player]);

  const loadAudio = async (data: LoadAudioAction) => {
    stopAllPlayback();
    dispatch(loadAudioAction(data));
  };

  const skip = useCallback(() => {
    if (queue.length - 1 >= queueIndex) {
      stopAllPlayback();
    }
    dispatch(skipToNextAudioAction());
  }, [dispatch, stopAllPlayback, queue?.length, queueIndex]);

  const skipBack = useCallback(() => {
    if (queueIndex > 0) {
      stopAllPlayback();
    }
    dispatch(skipBackToPreviousAudioAction());
  }, [dispatch, stopAllPlayback, queueIndex]);

  const playPauseAudio = useCallback(() => {
    dispatch(setIsPlayingAction(!audioPlayerPlaying));
  }, [audioPlayerPlaying, dispatch]);

  const stop = () => {
    stopAllPlayback();
    player?.empty();
    navigator.mediaSession?.setActionHandler('play', null);
    navigator.mediaSession?.setActionHandler('pause', null);
    navigator.mediaSession?.setActionHandler('previoustrack', null);
    navigator.mediaSession?.setActionHandler('nexttrack', null);
    dispatch(closePlayerAction());
  };

  const retryWaveformLoad = async (waveformSrc?: string) => {
    const peaks = await loadWaveformData(dispatch, waveformSrc);

    if (peaks) {
      player?.setOptions({ peaks: [peaks] });
    }
  };

  const repeat = (value: boolean) => {
    dispatch(setRepeatAction(value));
  };

  return {
    player,
    stop,
    playAudio: player?.play,
    pauseAudio: player?.pause,
    playPauseAudio,
    retryWaveformLoad,
    setIsWaveformOpen: (open: boolean) => dispatch(setIsWaveformOpen(open)),
    loadAudio,
    skip,
    skipBack,
    repeat,
  };
};
